import {t} from "../../i18n.js";
/*jshint esversion: 11 */

import Panzoom from '@panzoom/panzoom';

document.addEventListener('alpine:init', () => {
    Alpine.data('seatMapFlow', (config) => ({
        // Estado Inicial
        step: 1,
        selectedSeats: config.selectedSeats || [],
        localSeats: config.selectedSeats || [], // Sincroniza inicial
        activeSectorId: null,
        activeSectorName: t('Mapa do Evento'),
        mapPoints: config.mapPoints,
        panzoomInstance: null,
        isDesktopFullscreen: false,
        showPinchOverlay: false,

        // --- Mapa: geometria estática + disponibilidade ao vivo (ver EventSeatsController) ---
        mapState: 'idle', // idle | loading | ready | error
        mapStale: false,
        seatEls: [],
        taken: new Set(),
        blocked: new Set(),
        cartSeats: new Set(((config.seatMap && config.seatMap.cartSeats) || []).map(String)),
        seatToken: config.seatMap ? config.seatMap.token : null,
        availabilityTimer: null,
        availabilityUnchanged: 0,
        availabilityFailures: 0,
        availabilityStopped: false,
        lastAvailability: '',

        initFlow() {
            // Abre direto no mapa: escolher o setor antes de ver a planta só
            // induzia ao erro (quem não conhece a casa escolhia "plateia" e
            // clicava num assento da lateral, que tem outro preço). A lista de
            // setores e os ingressos sem lugar marcado continuam no passo 1,
            // alcançável pelo "Voltar" do mapa. Configurável por empresa
            // (config.skipCategoriesStep) — o servidor já força false quando
            // há ingresso sem lugar marcado, pois o mapa não vende esse tipo.
            if (this.localSeats.length > 0) {
                this.step = 3;
            } else if (config.skipCategoriesStep) {
                this.openSector('all', t('Mapa do evento'));
            }

            // Igual ao original: sem assento escolhido o rodapé fixo mobile aparece; com assento, some.
            const toggleFooter = (seats) => {
                const footer = document.getElementById('mobile-sticky-footer');
                if (footer) {
                    footer.style.display = seats.length > 0 ? 'none' : '';
                }
            };

            toggleFooter(this.localSeats);

            this.$watch('localSeats', (val) => {
                toggleFooter(val);
            });

            // TEMN-439: assento selecionado é reserva de verdade a partir do passo 3
            // (ver ChooseMarkedTicket::reserveSeats). Fechar a aba ou atualizar sem
            // querer não perde o assento — um refresh é recuperado sozinho (ver
            // rehydrateActiveReservation no componente) —, mas evita o cliente sair
            // do fluxo por engano achando que "só tá vendo o mapa". O texto do
            // preventDefault/returnValue é ignorado por todo navegador atual, que
            // sempre mostra a mensagem genérica própria dele.
            window.addEventListener('beforeunload', (event) => {
                if (this.localSeats.length > 0) {
                    event.preventDefault();
                    event.returnValue = '';
                }
            });

            // Watcher para controle de scroll do body
            this.$watch('step', (val) => {
                if (val === 2) {
                    if (window.innerWidth < 1024 || this.isDesktopFullscreen) {
                        document.body.style.overflow = 'hidden';
                    }
                    if (window.innerWidth < 1024) {
                        this.showPinchOverlay = true;
                    }
                } else {
                    document.body.style.overflow = '';
                    this.showPinchOverlay = false;
                }
            });

            this.loadMap();

            document.addEventListener('visibilitychange', this.onVisibility = () => {
                if (!document.hidden && this.mapState === 'ready') {
                    this.availabilityUnchanged = 0;
                    this.scheduleAvailability(0);
                }
            });

            // Troca de sessão/data: a disponibilidade anterior não vale mais. Só reage quando o servidor já aplicou a nova
            // sessão (o $wire.selected_session_id muda na resposta), então nunca busca com o id antigo.
            this.$wire.$watch('selected_session_id', () => {
                this.taken = new Set();
                this.blocked = new Set();
                this.lastAvailability = '';
                this.availabilityUnchanged = 0;
                this.paint();
                if (this.mapState === 'ready') this.refreshAvailability().catch(() => this.availabilityFailures++);
            });

            this.$watch('step', (val) => {
                if (val === 2 && this.mapState === 'ready') {
                    this.availabilityUnchanged = 0;
                    this.scheduleAvailability(0);
                }
            });

            this.$nextTick(() => {
                this.initPanzoom();
                const svgContainer = document.getElementById('main-svg');
                if (svgContainer) {
                    svgContainer.addEventListener('click', (e) => {
                        const seatEl = e.target.closest('.available-seat');
                        if (seatEl) this.toggleSeat(seatEl);
                    });
                }
            });
        },

        // --- Mapa ao vivo ---
        destroy() {
            clearTimeout(this.availabilityTimer);
            this.availabilityStopped = true;
            if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
        },

        seatHeaders() {
            return {Accept: '*/*', 'X-Page-Token': this.seatToken || ''};
        },

        /** Baixa o SVG puro do mapa (imutável por hash; o navegador o guarda) e começa a acompanhar a disponibilidade. */
        async loadMap() {
            if (!config.seatMap || this.mapState === 'loading') return;

            this.mapState = 'loading';

            try {
                const response = await fetch(config.seatMap.geometryUrl, {credentials: 'omit', headers: this.seatHeaders()});
                this.seatToken = response.headers.get('X-Page-Token') || this.seatToken;
                if (response.status === 404 || response.status === 403) this.mapStale = true; // mapa trocado ou token vencido: só uma recarga resolve
                if (!response.ok) throw new Error(response.status);

                this.$refs.svgHost.innerHTML = await response.text();
                this.seatEls = Array.from(this.$refs.svgHost.querySelectorAll('.seat'));
                this.mapState = 'ready';
                this.paint();

                // Setor aberto antes do mapa chegar: agora dá para enquadrar pela geometria (o panzoom nasce ~50 ms depois do init).
                if (this.step === 2 && this.activeSectorId) {
                    let attempts = 0;
                    const frame = () => {
                        if (!this.zoomToSector(this.activeSectorId) && !this.panzoomInstance && attempts++ < 20) setTimeout(frame, 100);
                    };
                    frame();
                }

            } catch (error) {
                this.mapState = 'error';

                return;
            }

            // O mapa já está pintado: uma falha na primeira disponibilidade (503, 429, rede) só adia o acompanhamento.
            try {
                await this.refreshAvailability();
            } catch (error) {
                this.availabilityFailures++;
            }

            this.scheduleAvailability(this.availabilityDelay());
        },

        /** Aplica o estado de cada assento. O assento do próprio cliente fica "selecionado" mesmo que o hold dele conste como tomado. */
        paint() {
            if (this.mapState !== 'ready') return;

            const preview = this.$refs.previewFlag && this.$refs.previewFlag.dataset.preview === '1';
            const mine = new Set(this.localSeats.map((seat) => String(seat.id)));

            for (const el of this.seatEls) {
                const id = el.id;
                let state;

                if (preview) state = 'seat preview-seat';
                else if (mine.has(id)) state = 'seat available-seat selected';
                else if (this.taken.has(id) || this.cartSeats.has(id)) state = 'seat occupied-seat';
                else if (this.blocked.has(id)) state = 'seat blocked-seat';
                else state = 'seat available-seat';

                if (el.getAttribute('class') !== state) el.setAttribute('class', state);
            }
        },

        async refreshAvailability() {
            const sessionId = this.$wire.selected_session_id;
            if (sessionId === null || sessionId === undefined) return;

            const response = await fetch(config.seatMap.availabilityUrl.replace('{session}', sessionId), {
                credentials: 'omit', cache: 'no-cache', headers: this.seatHeaders(),
            });
            this.seatToken = response.headers.get('X-Page-Token') || this.seatToken;

            // A sessão mudou enquanto a resposta vinha: ela é da sessão antiga, descarta.
            if (this.$wire.selected_session_id !== sessionId) return;

            if (response.status === 404 || response.status === 403) {
                this.availabilityStopped = true;

                return;
            }
            if (!response.ok) throw new Error(response.status);

            const data = await response.json();
            const fingerprint = data.taken.join(',') + '|' + data.blocked.join(',');

            if (fingerprint !== this.lastAvailability) {
                this.lastAvailability = fingerprint;
                this.taken = new Set(data.taken.map(String));
                this.blocked = new Set(data.blocked.map(String));
                this.availabilityUnchanged = 0;
                this.paint();
            } else {
                this.availabilityUnchanged++;
            }
        },

        availabilityDelay() {
            if (this.availabilityFailures > 0) return Math.min(30000, 3000 * 2 ** this.availabilityFailures);

            // Mapa vivo: 3 s enquanto muda, 5 s quando parado (teto de ~6 s de defasagem com o jitter).
            return this.availabilityUnchanged < 6 ? 3000 : 5000;
        },

        scheduleAvailability(ms) {
            if (this.availabilityStopped) return;

            clearTimeout(this.availabilityTimer);
            this.availabilityTimer = setTimeout(() => this.pollAvailability(), ms + Math.random() * 1000);
        },

        async pollAvailability() {
            // Só acompanha enquanto o mapa está à vista (passo 2) e a aba visível; retoma sozinho (visibilitychange / passo).
            if (this.availabilityStopped || document.hidden || this.step !== 2) return;

            try {
                await this.refreshAvailability();
                this.availabilityFailures = 0;
            } catch (error) {
                this.availabilityFailures++;
            }

            this.scheduleAvailability(this.availabilityDelay());
        },

        // --- Métodos de UI ---
        dismissPinchOverlay() {
            this.showPinchOverlay = false;
        },

        toggleFullscreen() {
            this.isDesktopFullscreen = !this.isDesktopFullscreen;
            document.body.style.overflow = this.isDesktopFullscreen ? 'hidden' : (window.innerWidth >= 1024 ? '' : 'hidden');

            setTimeout(() => {
                if (this.panzoomInstance) {
                    const elem = this.$refs.mapbgzoom;
                    const parent = elem.parentElement;
                    if (elem && parent && elem.offsetHeight > parent.offsetHeight) {
                        let newScale = parent.offsetHeight / elem.offsetHeight;
                        this.panzoomInstance.setOptions({minScale: newScale, startScale: newScale});
                    }
                    this.panzoomInstance.reset();
                }
            }, 350);
        },

        // --- Métodos de Ingressos Gerais ---
        addGeneralTicket(ticketId, name) {
            let tempId = `geral_${Date.now()}${Math.floor(Math.random() * 1000)}`;
            this.localSeats.push({
                index: this.localSeats.length,
                id: tempId,
                eventTicketId: ticketId,
                sector: null,
                quantity: 1,
                seat_label: name
            });
        },

        removeGeneralTicket(ticketId) {
            let index = [...this.localSeats].reverse().findIndex(s => s.eventTicketId === ticketId && String(s.id).startsWith('geral_'));
            if (index !== -1) {
                let realIndex = this.localSeats.length - 1 - index;
                this.localSeats.splice(realIndex, 1);
            }
        },

        getGeneralTicketCount(ticketId) {
            return this.localSeats.filter(s => s.eventTicketId === ticketId && String(s.id).startsWith('geral_')).length;
        },

        // --- Lógica do Mapa ---
        initPanzoom() {
            const elem = this.$refs.mapbgzoom;
            if (!elem) return;
            const parent = elem.parentElement;
            let startScale = 1;
            let startY = 0;

            setTimeout(() => {
                if (elem.offsetHeight > parent.offsetHeight && parent.offsetHeight > 0) {
                    startScale = parent.offsetHeight / elem.offsetHeight;
                    startY = 0 - ((elem.offsetHeight - parent.offsetHeight) / 2);
                }

                this.panzoomInstance = Panzoom(elem, {
                    maxScale: 6,
                    minScale: startScale,
                    startScale: startScale,
                    startY: startY,
                    step: 0.3,
                    excludeClass: 'seat selectionable-sector'
                });

                parent.addEventListener('wheel', this.panzoomInstance.zoomWithWheel);
            }, 50);
        },

        openSector(sectorMapId, sectorName) {
            this.activeSectorId = sectorMapId === 'all' ? null : sectorMapId;
            this.activeSectorName = sectorName;
            this.step = 2;

            this.$nextTick(() => {
                // Enquadramento pela geometria do próprio SVG: vale para
                // qualquer mapa setorizado, sem precisar do JSON de pontos
                // desenhado à mão (que só existe em um dos mapas).
                if (sectorMapId !== 'all' && !(this.mapPoints && this.mapPoints[sectorMapId])) {
                    if (this.zoomToSector(sectorMapId)) return;
                }

                if (this.panzoomInstance) this.panzoomInstance.reset();

                if (sectorMapId !== 'all' && this.mapPoints && this.mapPoints[sectorMapId]) {
                    const position = this.mapPoints[sectorMapId].position;
                    const mapBgZoom = this.$refs.mapbgzoom;

                    mapBgZoom.style.transition = 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
                    mapBgZoom.style.transformOrigin = position.origin;
                    mapBgZoom.style.transform = position.transform;

                    setTimeout(() => {
                        mapBgZoom.style.transition = 'none';
                        const style = window.getComputedStyle(mapBgZoom);
                        const matrix = style.getPropertyValue('transform');

                        if (matrix !== 'none') {
                            const values = matrix.match(/matrix\((.+)\)/)[1].split(', ');
                            this.panzoomInstance.zoom(parseFloat(values[0]), {animate: false});
                            this.panzoomInstance.pan(parseFloat(values[4]), parseFloat(values[5]), {force: true});
                        }
                    }, 650);
                } else {
                    const mapBgZoom = this.$refs.mapbgzoom;
                    if (!mapBgZoom) return; // o painel do mapa já saiu do DOM (ex.: navegando para o carrinho)
                    mapBgZoom.style.transition = 'none';
                    mapBgZoom.style.transform = 'none';
                }
            });
        },

        /**
         * Enquadra o setor usando a geometria real das formas com
         * data-sectorid. Retorna false quando não dá pra enquadrar (mapa sem
         * setorização, categoria sem setor, panzoom ainda não iniciado), pro
         * chamador cair no comportamento antigo de mostrar o mapa inteiro.
         */
        zoomToSector(sectorMapId) {
            const pz = this.panzoomInstance;
            const parent = document.getElementById('mapcontainer');

            if (!pz || !parent || !sectorMapId) return false;

            const shapes = document.querySelectorAll(
                `#main-svg [data-sectorid="${CSS.escape(String(sectorMapId))}"]`
            );

            if (!shapes.length) return false;

            // Parte de um estado conhecido: o fallback do openSector escreve
            // transform direto no elemento, o que dessincroniza o estilo do
            // estado interno do panzoom.
            pz.reset({animate: false});

            // O panzoom aplica o transform dentro de um requestAnimationFrame,
            // então só dá pra medir no frame seguinte ao de cada ajuste.
            requestAnimationFrame(() => {
                const parentRect = parent.getBoundingClientRect();
                const box = this.unionRect(shapes);

                if (!box.width || !box.height || !parentRect.width) return;

                const maxScale = pz.getOptions().maxScale || 6;
                const fit = Math.min(parentRect.width / box.width, parentRect.height / box.height) * 0.8;
                const scale = Math.min(pz.getScale() * fit, maxScale);

                pz.zoom(scale, {animate: false});

                requestAnimationFrame(() => {
                    const zoomed = this.unionRect(shapes);
                    const pan = pz.getPan();

                    // O translate entra depois do scale
                    // (`scale(s) translate(x, y)`), por isso o deslocamento
                    // medido em pixels de tela vai dividido pela escala.
                    pz.pan(
                        pan.x + (parentRect.left + parentRect.width / 2 - (zoomed.left + zoomed.width / 2)) / scale,
                        pan.y + (parentRect.top + parentRect.height / 2 - (zoomed.top + zoomed.height / 2)) / scale,
                        {animate: true, force: true}
                    );
                });
            });

            return true;
        },

        /** Retângulo de tela que envolve todos os elementos. */
        unionRect(elements) {
            let left = Infinity, top = Infinity, right = -Infinity, bottom = -Infinity;

            elements.forEach((el) => {
                const rect = el.getBoundingClientRect();

                if (!rect.width && !rect.height) return;

                left = Math.min(left, rect.left);
                top = Math.min(top, rect.top);
                right = Math.max(right, rect.right);
                bottom = Math.max(bottom, rect.bottom);
            });

            return {left, top, width: right - left, height: bottom - top};
        },

        backToSectors() {
            this.step = 1;
            this.activeSectorId = null;
            this.activeSectorName = t('Mapa do Evento');
            if (this.panzoomInstance) this.panzoomInstance.reset();
        },

        toggleSeat(el) {
            const seatId = el.id;
            if (!el.classList.contains('available-seat')) {
                return;
            }

            if (navigator.vibrate) {
                navigator.vibrate(30);
            }
            if (this.showPinchOverlay) {
                this.dismissPinchOverlay();
            }
            this.$refs.floatingBar.scrollIntoView({behavior: 'smooth', block: 'end'});

            const index = this.localSeats.findIndex(s => s.id === seatId);

            if (index === -1) {
                el.classList.add('selected');
                this.localSeats.push({
                    index: this.localSeats.length,
                    id: seatId,
                    eventTicketId: null,
                    sector: el.dataset.sectorid || el.getAttribute('data-sectorid') || this.activeSectorId,
                    quantity: 1,
                    seat_label: el.dataset.label || el.getAttribute('data-label') || seatId
                });
            } else {
                el.classList.remove('selected');
                this.localSeats.splice(index, 1);
            }
        },

        confirmSeatsAndSync() {
            this.step = 3;
            this.$wire.syncSeats(this.localSeats);
        },

        removeSeatFromCheckout(seatId, indexVal) {
            this.localSeats.splice(indexVal, 1);

            if (seatId && !String(seatId).startsWith('geral_')) {
                const el = document.getElementById(seatId);
                if (el) el.classList.remove('selected');
                this.$wire.removeSeat(seatId);
            } else {
                this.$wire.set('selectedSeats', this.localSeats);
            }

            if (this.localSeats.length === 0) {
                this.openSector('all', t('Mapa do evento'));
                if (this.panzoomInstance) setTimeout(() => this.panzoomInstance.reset(), 100);
            }
        },

        /**
         * TEMN-439: remoção precisa de assento(s) perdido(s) pra outra pessoa,
         * sem mexer no resto da seleção — ao contrário de resetSeats(), que
         * descarta o carrinho inteiro e volta pro mapa. Usado quando o servidor
         * recusa parte de uma reserva (evento 'seats-lost'); resetSeats() continua
         * sendo disparado à parte quando a perda esvazia o carrinho inteiro.
         */
        removeLostSeats(seatIds) {
            (seatIds || []).forEach((id) => {
                const index = this.localSeats.findIndex(s => String(s.id) === String(id));
                if (index !== -1) this.localSeats.splice(index, 1);

                // O servidor recusou: o assento é de outra pessoa. Marca já como ocupado, sem esperar o próximo ciclo.
                this.taken.add(String(id));
            });
            this.paint();
        },

        /**
         * Usado pelo clique de trocar sessão/data (@click="$wire.setSessionId(...);
         * resetSeats()"), onde o SERVIDOR ainda não sabe que o carrinho zerou —
         * precisa do $wire.set pra avisar.
         *
         * TEMN-439: o listener do evento seats-reset (perda de reserva) NÃO usa
         * este método — usa resetSeatsLocally() direto. Motivo: aqui o servidor já
         * apagou $selectedSeats sozinho antes de disparar o evento, então o
         * $wire.set seria uma segunda requisição redundante — e foi exatamente
         * essa requisição extra que causava o bug visual documentado em
         * forceHideCheckoutPanel(): o morph da SUA resposta, chegando depois,
         * apagava o estilo que a correção acabara de aplicar (o HTML do servidor
         * não tem o atributo style que só existe no cliente). Eliminar a
         * requisição resolveu a causa, não só o sintoma.
         */
        resetSeats() {
            this.$wire.set('selectedSeats', []);
            this.resetSeatsLocally();
        },

        resetSeatsLocally() {
            this.localSeats = [];
            this.openSector('all', t('Mapa do evento'));
            document.body.style.overflow = '';
            if (this.panzoomInstance) this.panzoomInstance.reset();
            document.querySelectorAll('.available-seat.selected').forEach(el => el.classList.remove('selected'));
            this.paint();
        }
    }));
});
